import React from "react";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import requests from "../Auth/Requests";
import globals from '../Globals'
import DatabasePage from "./DatabasePage";
import AssetsPage from "./AssetsPage";
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, Button, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel } from '@mui/material';

export default class AssetInspector extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedBranch: "ALL",
            selectedPlatform: "ALL",
            assetName: "",
            assetInfo: null,
            orderBy: "branch",
            orderDirection: "asc",
        };
    }

    performRequest(description, ...args) {
        let payload = {};
        if (typeof args[0] === "object" && args[0] !== null) {
            payload = args[0];
            args = args.slice(1);
        }
        const cmd = args.join(" ");
        const key = enqueueSnackbar(`${description}...`, { variant: 'info', persist: true });

        return requests.request("versioning " + cmd, payload)
            .then(response => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} successful!`, { variant: 'success' });
                return response;
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} failed: ${error.message}`, { variant: 'error' });
            });
    }

    handleBranchChange = (event) => {
        this.setState({ selectedBranch: event.target.value }, () => {
            this.updateHashWithSelectors();
            this.handleInspectClick();
        });
    };

    handlePlatformChange = (event) => {
        this.setState({ selectedPlatform: event.target.value }, () => {
            this.updateHashWithSelectors();
            this.handleInspectClick();
        });
    };

    handleAssetNameChange = (event) => {
        this.setState({ assetName: event.target.value }, () => this.updateHashWithSelectors());
    };

    handleInspectClick = () => {
        const { selectedPlatform, selectedBranch, assetName } = this.state;
        this.performRequest("Fetch asset info", "get-asset-info", selectedPlatform, selectedBranch, assetName, "ALL")
            .then((response) => {
                this.setState({ assetInfo: response }, () => this.updateHashWithSelectors());
            })
            .catch((error) => {
                enqueueSnackbar("Failed to fetch asset info", { variant: "error" });
                this.setState({ assetInfo: null }, () => this.updateHashWithSelectors());
            });
    };
    // Helper: update hash in URL with selectors, preserving first two segments if present
    updateHashWithSelectors = () => {
        const { selectedBranch, selectedPlatform, assetName } = this.state;
        // Get the current hash (without #)
        let hashBody = window.location.hash.startsWith("#") ? window.location.hash.substring(1) : window.location.hash;
        let segments = hashBody.length > 0 ? hashBody.split("-") : [];
        // Preserve first two segments (e.g. page and subpage) if present
        const preserved = segments.slice(0, 2);
        // Encode selectors
        const selectors = [
            encodeURIComponent(selectedBranch),
            encodeURIComponent(selectedPlatform),
            encodeURIComponent(assetName)
        ];
        // New hash: preserved + selectors
        const newSegments = [...preserved, ...selectors];
        const newHash = "#" + newSegments.join("-");
        if (window.location.hash !== newHash) {
            window.history.replaceState(null, '', newHash);
        }
    }

    // Helper: parse hash and return selectors, or null if not present
    getSelectorsFromHash = () => {
        const hash = window.location.hash;
        if (!hash || hash.length < 2) return null;
        // Remove leading "#"
        const hashBody = hash.substring(1);
        const parts = hashBody.split("-");
        // Skip first two segments (page/subpage), selectors follow
        if (parts.length < 5) return null; // at least 2 preserved + 3 selectors
        try {
            return {
                selectedBranch: decodeURIComponent(parts[2]),
                selectedPlatform: decodeURIComponent(parts[3]),
                assetName: decodeURIComponent(parts.slice(4).join("-")), // assetName might have dashes
            };
        } catch (e) {
            return null;
        }
    }

    componentDidMount() {
        // Check hash and set selectors if present
        const selectors = this.getSelectorsFromHash();
        if (selectors) {
            this.setState({
                selectedBranch: selectors.selectedBranch,
                selectedPlatform: selectors.selectedPlatform,
                assetName: selectors.assetName,
            }, () => {
                this.updateHashWithSelectors();
                // Optionally fetch asset info immediately if assetName is not empty
                if (selectors.assetName && selectors.assetName.trim() !== "") {
                    this.handleInspectClick();
                }
            });
        }
    }

    handleRequestSort = (property) => {
        const isAsc = this.state.orderBy === property && this.state.orderDirection === "asc";
        this.setState({
            orderBy: property,
            orderDirection: isAsc ? "desc" : "asc",
        });
    };

    stableSort(array, comparator) {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    }

    getComparator(order, orderBy) {
        return order === "desc"
            ? (a, b) => this.descendingComparator(a, b, orderBy)
            : (a, b) => -this.descendingComparator(a, b, orderBy);
    }

    descendingComparator(a, b, orderBy) {
        // For branch and platform, compare using globals mapping to strings
        if (orderBy === "branch") {
            const aVal = globals.branches[a.branch] || a.branch;
            const bVal = globals.branches[b.branch] || b.branch;
            if (bVal < aVal) return -1;
            if (bVal > aVal) return 1;
            return 0;
        }
        if (orderBy === "platform") {
            const aVal = globals.platformNames[a.platform] || a.platform;
            const bVal = globals.platformNames[b.platform] || b.platform;
            if (bVal < aVal) return -1;
            if (bVal > aVal) return 1;
            return 0;
        }
        if (orderBy === "version") {
            if (b.version < a.version) return -1;
            if (b.version > a.version) return 1;
            return 0;
        }
        if (orderBy === "size") {
            return b.size - a.size;
        }
        if (orderBy === "createdVersion") {
            // Decompact versions and compare major, minor, patch descending
            const bVer = globals.decompactVersion(b.createdVersion);
            const aVer = globals.decompactVersion(a.createdVersion);
            if (bVer.major !== aVer.major) return bVer.major - aVer.major;
            if (bVer.minor !== aVer.minor) return bVer.minor - aVer.minor;
            if (bVer.patch !== aVer.patch) return bVer.patch - aVer.patch;
            return 0;
        }
        if (orderBy === "date") {
            const bDate = new Date(b.createdDate || b.date || 0);
            const aDate = new Date(a.createdDate || a.date || 0);
            return bDate - aDate;
        }
        return 0;
    }

    renderAssetInfoTable() {
        const { assetInfo, orderBy, orderDirection } = this.state;
        if (!assetInfo || assetInfo.length === 0) {
            return null;
        }

        const headers = ["branch", "platform", "version", "size", "createdVersion", "date"];

        // Apply sorting
        let sortedData = [];
        if (orderBy === "branch" && orderDirection === "asc") {
            // Default ordering: branch, then platform, then version ascending
            sortedData = [...assetInfo].sort((a, b) => {
                const aBranch = globals.branches[a.branch] || a.branch;
                const bBranch = globals.branches[b.branch] || b.branch;
                if (aBranch < bBranch) return -1;
                if (aBranch > bBranch) return 1;
                const aPlatform = globals.platformNames[a.platform] || a.platform;
                const bPlatform = globals.platformNames[b.platform] || b.platform;
                if (aPlatform < bPlatform) return -1;
                if (aPlatform > bPlatform) return 1;
                if (a.version < b.version) return -1;
                if (a.version > b.version) return 1;
                return 0;
            });
        } else {
            sortedData = this.stableSort(assetInfo, this.getComparator(orderDirection, orderBy));
        }

        return (
            <TableContainer component={Paper} sx={{ mt: 3, p: 2, borderRadius: 2, boxShadow: 1, bgcolor: 'background.paper' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headers.map((header) => (
                                <TableCell key={header} sx={{ fontWeight: 'bold' }} sortDirection={orderBy === header ? orderDirection : false}>
                                    <TableSortLabel
                                        active={orderBy === header}
                                        direction={orderBy === header ? orderDirection : "asc"}
                                        onClick={() => this.handleRequestSort(header)}
                                    >
                                        {header}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedData.map((row, index) => {
                            const branchValue = globals.branches[row.branch];
                            const platformValue = globals.platformNames[row.platform];
                            const dateValue = row.createdDate || row.date || "";
                            const { major, minor, patch } = globals.decompactVersion(row.createdVersion);
                            return (
                                <TableRow key={index}>
                                    <TableCell>{branchValue}</TableCell>
                                    <TableCell>{platformValue}</TableCell>
                                    <TableCell>{row.version}</TableCell>
                                    <TableCell>{AssetsPage.formatSize(row.size)}</TableCell>
                                    <TableCell>{`${major}.${minor}.${patch}`}</TableCell>
                                    <TableCell>{dateValue ? new Date(dateValue).toLocaleString() : ""}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    render() {
        const { selectedBranch, selectedPlatform, assetName } = this.state;

        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="branch-label">Branch</InputLabel>
                        <Select
                            labelId="branch-label"
                            value={selectedBranch}
                            onChange={this.handleBranchChange}
                            label="Branch"
                        >
                            <MenuItem value="ALL">ALL</MenuItem>
                            {globals.branches.map((branch) => (
                                <MenuItem key={branch} value={branch}>{branch}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="platform-label">Platform</InputLabel>
                        <Select
                            labelId="platform-label"
                            value={selectedPlatform}
                            onChange={this.handlePlatformChange}
                            label="Platform"
                        >
                            <MenuItem value="ALL">ALL</MenuItem>
                            {globals.platformNames.map((platform) => (
                                <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Asset Name"
                        variant="outlined"
                        size="small"
                        value={assetName}
                        onChange={this.handleAssetNameChange}
                        sx={{ minWidth: 160 }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                this.handleInspectClick();
                            }
                        }}
                    />

                    <Button variant="contained" onClick={this.handleInspectClick} sx={{ height: 40 }}>
                        INSPECT
                    </Button>
                </Box>

                <Box>
                    {this.renderAssetInfoTable()}
                </Box>
            </>
        );
    }
}